import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { blogPosts } from '../../../mocks/blog-posts';
import ArticleContent from './components/ArticleContent';
import RelatedArticles from './components/RelatedArticles';
import ShareButtons from './components/ShareButtons';

export default function BlogArticlePage() {
  const { slug } = useParams();
  const article = blogPosts.find(post => post.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.alwaz.travel';
    const scripts: HTMLScriptElement[] = [];

    if (article) {
      document.title = `${article.title} | Alwaz Travels Travel Guides`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', article.excerpt || `Read ${article.title} – expert travel advice from Alwaz Travels London.`);

      const schemas = [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt || '',
          image: article.image || '',
          author: {
            '@type': 'Person',
            name: article.author || 'Alwaz Travels',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Alwaz Travels',
            url: siteUrl,
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/vite.svg`,
            },
          },
          datePublished: article.date || '',
          dateModified: article.date || '',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/travel-guides/${slug}`,
          },
          url: `${siteUrl}/travel-guides/${slug}`,
          articleSection: article.category || 'Travel',
          inLanguage: 'en-GB',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Travel Guides', item: `${siteUrl}/travel-guides` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `${siteUrl}/travel-guides/${slug}` },
          ],
        },
      ];

      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        scripts.push(script);
      });
    }

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, [slug, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl font-bold text-[#1A1F2E] mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <a href="/travel-guides" className="inline-block px-6 py-3 bg-[#C9A961] text-white rounded-lg hover:bg-[#B89851] transition-colors whitespace-nowrap cursor-pointer">
            Back to Travel Guides
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Article Header */}
      <article className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <a href="/" className="hover:text-[#C9A961] transition-colors cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/travel-guides" className="hover:text-[#C9A961] transition-colors cursor-pointer">Travel Guides</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-700">{article.category}</span>
          </nav>

          {/* Category Badge */}
          <div className="mb-4">
            <span className="inline-block px-4 py-2 bg-[#C9A961]/10 text-[#C9A961] text-sm font-semibold rounded-full">
              {article.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-[#1A1F2E] mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            {article.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden">
                <img
                  src={article.authorImage}
                  alt={article.author}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="font-semibold text-[#1A1F2E]">{article.author}</p>
                <p className="text-sm text-gray-500">Travel Expert</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="relative h-96 w-full rounded-2xl overflow-hidden mb-12">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Share Buttons */}
          <ShareButtons title={article.title} />

          {/* Article Content */}
          <ArticleContent slug={slug || ''} />
        </div>
      </article>

      {/* Related Articles */}
      <RelatedArticles currentSlug={slug || ''} currentCategory={article.category} />

      <Footer />
    </div>
  );
}
